import React from 'react'
import styled, { css } from 'styled-components'
import { secondsToString, secondsToFullString } from '@mote/helpers'
import { Slider, VisuallyHidden } from '@mote/ui'
import { useMotePlayer } from '../contexts/motePlayer.context'

export const Scrubber = ({
  variant = 'lg',
  disabled = false,
  loading = false,
  currentTime = 0,
  totalTime = 0,
  ...rest
}) => {
  const {
    pause,
    play,
    playing,
    position,
    duration,
    onScrubberValueChange: handleValueChange,
    onScrubberValueChangeEnd: handleValueChangeEnd
  } = useMotePlayer()

  const parseTotalTime = React.useCallback(() => {
    return totalTime === '{{ totalTime }}' || typeof totalTime === 'undefined'
      ? 0
      : totalTime
  }, [totalTime])

  const screenReaderText = React.useMemo(() => {
    const played = secondsToFullString(position)
    const total = secondsToFullString(duration || parseTotalTime())

    return `Played: ${played} of total duration (${total})`
  }, [position, duration, parseTotalTime])

  if (loading) {
    return (
      <ScrubberLoadingBar variant={variant}>
        <ScrubberLoadingMeter />
      </ScrubberLoadingBar>
    )
  }

  return (
    <Slider
      aria-label='Audio timeline'
      size='lg'
      value={position.toFixed(2)}
      min={0}
      max={duration || parseTotalTime()}
      getAriaValueText={value => secondsToFullString(value)}
      onChange={handleValueChange}
      onChangeEnd={handleValueChangeEnd}
      focusThumbOnChange={false}
      isDisabled={disabled}
      {...rest}
    >
      <Slider.Track>
        <StyledSliderTrackFilled />
      </Slider.Track>
      <StyledSliderThumb />
      <VisuallyHidden>{screenReaderText}</VisuallyHidden>
    </Slider>
  )
}

const ScrubberLoadingBar = styled.div`
  background: rgba(92, 112, 128, 0.2);
  border-radius: 40px;
  display: block;
  height: 6px;
  overflow: hidden;
  position: relative;
  width: 100%;
  margin-bottom: ${({ variant }) => (variant === 'sm' ? 0 : 8)}px;

  @keyframes linear-progress-bar-stripes {
    from {
      background-position: 0 0;
    }

    to {
      background-position: 29px 0;
    }
  }
`

const ScrubberLoadingMeter = styled.div`
  animation: linear-progress-bar-stripes 0.3s linear infinite reverse;
  background: linear-gradient(
    -45deg,
    hsla(0, 0%, 100%, 0.2) 25%,
    transparent 0,
    transparent 50%,
    hsla(0, 0%, 100%, 0.2) 0,
    hsla(0, 0%, 100%, 0.2) 75%,
    transparent 0
  );
  background-color: rgba(172, 10, 232, 0.75);
  background-size: 30px 30px;
  border-radius: 40px;
  height: 100%;
  position: absolute;
  -webkit-transition: width 0.2s cubic-bezier(0.4, 1, 0.75, 0.9);
  transition: width 0.2s cubic-bezier(0.4, 1, 0.75, 0.9);
  width: 100%;
`

const StyledSliderThumb = styled(Slider.Thumb)`
  width: 12px;
  height: 12px;
  background: #ac0ae7;
  box-shadow: none;
  left: calc(-12px / 2);

  &:disabled,
  &[data-disabled] {
    background: #d1d1e0;
  }

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      background: #d1d1e0;
    `};
`

const StyledSliderTrackFilled = styled(Slider.TrackFilled)`
  width: 0%;
`
